/**
 * Forward-only page-navigation policy for the intake form.
 *
 * Callers pass the already-visible section list from `visibleSections()`, rather than the
 * raw schema. That makes hidden/inapplicable sections disappear from the gate exactly as
 * they do from the sidebar progress counter and validation output.
 */

import { isSectionComplete } from './fieldRules.js';

/** Index of the first incomplete applicable section, or -1 when every visible section is complete. */
export function firstIncompleteSectionIndex(sections = [], bySection = {}) {
  return sections.findIndex((section) => !isSectionComplete(bySection[section.id] || []));
}

/**
 * Whether a problem stops navigation even where the firm has turned the page gate off.
 *
 * The page gate is a convenience about UNFINISHED work — it lets people fill pages in any order.
 * A problem marked `blocksNavigation` is a different thing: data that is internally impossible,
 * like an origination split adding to 120%. Letting someone carry that forward and discover it at
 * submit, several pages later, is exactly the kind of late surprise the gate exists to avoid.
 */
function hasBlockingProblem(problems = []) {
  return problems.some((problem) => problem && problem.blocksNavigation);
}

/** The current page may advance only after its own required fields are complete. */
export function canAdvanceFromSection(section, bySection = {}, pageGateDisabled = false) {
  if (!section) return false;
  const problems = bySection[section.id] || [];
  if (hasBlockingProblem(problems)) return false;
  if (pageGateDisabled) return true;
  return isSectionComplete(problems);
}

/**
 * Backward sidebar navigation is always available. A forward destination is available only
 * through the first incomplete applicable section, never beyond it.
 */
export function canNavigateToSection({
  currentIndex,
  targetIndex,
  sections = [],
  bySection = {},
  pageGateDisabled = false,
}) {
  // Moving BACK is always allowed, even past invalid data: someone has to be able to return to
  // the page holding the problem in order to fix it.
  if (targetIndex <= currentIndex) return true;
  if (hasBlockingProblem(bySection[sections[currentIndex]?.id] || [])) return false;
  if (pageGateDisabled) return true;
  const firstIncomplete = firstIncompleteSectionIndex(sections, bySection);
  return firstIncomplete === -1 || targetIndex <= firstIncomplete;
}
